import { Injectable, Injector } from '@angular/core';
// rxjs
import { BehaviorSubject, Observable } from 'rxjs';
// models
import {CartItem, MenuItemForPayment} from '../models/cart-item.model';
import {GroupOrderTab, Loyalty, Order, OrderMap, OrderTab} from '../models/payment-item.model';
import { Discount, DiscountOrder, Tax } from '../models/restaurant.model';
import {DeliveryPreEstimateResponseData, DeliveryResponseData, SubscriptionPhase, DeliveryType} from '../models/order.model';
import { RestaurantStore } from './restaurant.store';
import { take } from 'rxjs/operators';
import { PaymentStore } from './payment.store';
import { Tip } from '../enums/tip.enum';
import {parseDuration} from '../dto/restaurant.dto';

@Injectable()
export class CartStore {
  private _items = new BehaviorSubject<MenuItemForPayment[]>([]);
  private _discounts = new BehaviorSubject<any[]>([]);
  private _coupons = new BehaviorSubject<DiscountOrder[]>([]);
  private _orderMap = new BehaviorSubject<OrderMap>({});
  private _loyalty = new BehaviorSubject<Loyalty>(new Loyalty());
  private _isLoyaltyEnabled = new BehaviorSubject<boolean>(false);
  private _tab = new BehaviorSubject<OrderTab>(null);
  private _groupTab = new BehaviorSubject<GroupOrderTab>(null);
  private _tabClosedSuccessful = new BehaviorSubject<boolean>(false);
  private _showTabModal = new BehaviorSubject<boolean>(false);
  private _updateCartInProgress = new BehaviorSubject<boolean>(false);
  private _subscriptionsData = new BehaviorSubject<SubscriptionPhase[]>(null);
  private _deliveryResponseData = new BehaviorSubject<DeliveryResponseData>(null);
  private _deliveryDurationData = new BehaviorSubject<DeliveryPreEstimateResponseData>(null);
  private _tip = new BehaviorSubject<number>(0);
  private _tipDriver = new BehaviorSubject<number>(0);
  private _pickupAt: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private _isFutureOrderError: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _pickupLocation = null;
  private _deliveryAt = null;
  private _isAsap: boolean = true;
  private _printAheadDuration = null;
  private _pickupWindowDuration = null;
  private _autoGratuityPercent: number = 0;
  private _driverGratuityPercent: number = 0;
  private _enableRestaurantTip: boolean = false;
  private _kyooFee: number;
  private _additionalKyooFee: number;
  private _deliveryFee: number;
  private _ordersInGroup = 0;
  private _tippingEnabled: boolean;
  private _orderConfirmationText = null;
  private _loyaltyProgramEnabled = new BehaviorSubject<boolean>(null);
  private tipType = Tip;
  public deliveryType: DeliveryType|string = DeliveryType.SELLER;
  constructor(private injector: Injector, private paymentStore: PaymentStore) { }

  static compareToItem(firstItem, secondItem) {
    const copyFirstItem = JSON.parse(JSON.stringify(firstItem));
    const copySecondItem = JSON.parse(JSON.stringify(secondItem));
    delete copyFirstItem.item.quantity;
    delete copySecondItem.item.quantity;
    return JSON.stringify(copyFirstItem) === JSON.stringify(copySecondItem);
  }

  public get items$(): Observable<MenuItemForPayment[]> {
    return this._items.asObservable();
  }

  public setCartItems(items: MenuItemForPayment[]): void {
    this._items.next(items);
  }

  public get isFutureOrderError(): boolean {
    return this._isFutureOrderError.value;
  }

  public get items(): MenuItemForPayment[] {
    return this._items.value;
  }

  public get tab(): OrderTab {
    return this._tab.getValue();
  }

  public get tab$(): Observable<OrderTab> {
    return this._tab.asObservable();
  }

  public get groupTab(): GroupOrderTab {
    return this._groupTab.getValue();
  }

  public get loyalty$(): Observable<Loyalty> {
    return this._loyalty.asObservable();
  }

  public get loyalty(): Loyalty {
    return this._loyalty.getValue();
  }

  public get cartItems(): CartItem[] {
    return this.items.map(item =>
      new CartItem(
        item.item.name,
        item.item.variation,
        item.item.modifiers,
        item.item.quantity,
        item.item.specialInstructions,
        item.item.totalItemPrice,
        item.locationId,
        item.categoryId,
        item.isService,
        item.isFutureOrder,
        item.throttlingEstimation,
        item?.metadata
      ));
  }

  public get subscriptionsData(): SubscriptionPhase[] {
    return this._subscriptionsData.value;
  }

  public get deliveryResponseData(): DeliveryResponseData {
    return this._deliveryResponseData.value;
  }

  public get deliveryDurationData(): DeliveryPreEstimateResponseData {
    return this._deliveryDurationData.value;
  }

  public get totalQuantity(): number {
    return this.items.reduce((sum, el) => sum + el.item.quantity, 0);
  }

  public get pickupAt(): string {
    return this._pickupAt.value;
  }

  public get pickupLocation(): string {
    return this._pickupLocation;
  }

  public get deliveryAt(): string {
    return this._deliveryAt;
  }

  public get isAsap(): boolean {
    return this._isAsap;
  }

  public get printAheadDuration(): string {
    return this._printAheadDuration || 'PT300S';
  }

  public get pickupWindowDuration(): string {
    return this._pickupWindowDuration || 'PT15M';
  }

  public get autoGratuityPercent(): number {
    return this._autoGratuityPercent;
  }

  public getGratuityPercent(tipType: Tip, isDelivery: boolean = false,
                            deliveryType: DeliveryType|string = DeliveryType.SELLER,
                            isCourierEnable: boolean = false, isSellerEnable: boolean = false): number {
    this.deliveryType = deliveryType;
    if (isDelivery) {
      if (isCourierEnable && isSellerEnable) {
        if (this._enableRestaurantTip && deliveryType === DeliveryType.COURIER) {
          return this.getGratuity(tipType, '_driverGratuityPercent');
        }
      } else if (isCourierEnable && this._enableRestaurantTip ) {
        return this.getGratuity(tipType, '_driverGratuityPercent');
      }
      return this._driverGratuityPercent;
    }
    return this._autoGratuityPercent;
  }

  private getGratuity(tipType: Tip, type: string): number {
    switch (tipType) {
      case Tip.restaurant: return this[type] / 2;
      case Tip.driver: return this[type] / 2;
    }
  }

  public get groupTabKyooFee(): number {
    let kyooFee = 0;
    for (const round of this.groupTab.rounds) {
      const locationsInRound = [];
      for (const order of round.round_orders) {
        locationsInRound.push(order.location_id);
      }
      const uniqueLocations = locationsInRound.filter((v, i, a) => a.indexOf(v) === i);
      kyooFee += this._kyooFee + (this._additionalKyooFee * (uniqueLocations.length - 1));
    }
    return kyooFee;
  }

  public get isLoyaltyEnabled(): boolean {
    return this._isLoyaltyEnabled.getValue();
  }

  public get kyooFee(): number {
    if (this.additionalKyooFee) {
      return this._kyooFee + (this._additionalKyooFee * (this._ordersInGroup - 1));
    }
    return this._kyooFee || 0;
  }

  public get deliveryFee(): number {
    if (this._deliveryFee) {
      return this._deliveryFee;
    } else if (this.deliveryResponseData) {
      return (this.deliveryResponseData.delivery_fee_cents + this.deliveryResponseData.kyoo_fee_cents) || 0;
    } else {
      return 0;
    }
  }

  public get additionalKyooFee(): number {
    return this._additionalKyooFee || 0;
  }

  public get tippingEnabled(): boolean {
    return this._tippingEnabled;
  }

  /**
   * This getter returns a total price of entire cart
   * @return total price of entire cart
   */
  public get totalPrice(): number {
    let totalPrice = 0;
    const orderMap = this.orderMap;

    const locationsIds = Object.keys(orderMap);
    for (const locationId of locationsIds) {
      totalPrice += orderMap[locationId].amount / 100;
    }
    return totalPrice;
  }

  /**
   * This getter returns the flat rate tip amount
   * @return flat rate tip
   */
  public getTip(tipType: Tip = Tip.restaurant): number {
    switch (tipType) {
      case Tip.driver: return this._tipDriver.getValue();
      case Tip.restaurant: return this._tip.getValue();
    }
  }

  public get tips(): number {
    return this.getTip(this.tipType.restaurant) + this.getTip(this.tipType.driver);
  }

  public get tip$(): Observable<number> {
    return this._tip.asObservable();
  }

  public get taxesPrice(): number {
    const taxes: Tax[] = this.exclusiveTaxesFromOrders;
    let totalTaxesPrice = 0;
    if (this.totalPrice > 0.99) {
      for (const tax of taxes) {
        totalTaxesPrice += tax.applied_money.amount / 100;
      }
    }
    return totalTaxesPrice;
  }

  public get totalTaxesPrice(): number {
    if (this.totalPrice > 0.99) {
      return this.taxesPrice + this._kyooFee / 100;
    }
    return this.taxesPrice;
  }

  public get groupTaxesPrice(): number {
    if (this.totalPrice > 0.99) {
      return this.taxesPrice + this.kyooFee / 100;
    }
    return this.taxesPrice;
  }

  public get groupTabTaxesPrice(): number {
    if (this.totalPrice > 0.99) {
      return this.taxesPrice + this.groupTabKyooFee / 100;
    }
    return this.taxesPrice;
  }

  public get totalDiscountsPrice(): number {
    let discounts = 0;

    const orderMap = this.orderMap;

    const locationsIds = Object.keys(orderMap);
    locationsIds.forEach((locationId: string) => {
      if (orderMap[locationId] && orderMap[locationId].discounts) {
        for (const discount of orderMap[locationId].discounts) {
          discounts += discount.applied_money.amount / 100;
        }
      }
    });
    return discounts;
  }

  public get discountsFromOrders(): Discount[] {
    const discounts: Discount[] = [];

    const orderMap = this.orderMap;

    const locationsIds = Object.keys(orderMap);
    for (const locationId of locationsIds) {
      if (orderMap[locationId].discounts) {
        for (const discount of orderMap[locationId].discounts) {
          discounts.push(discount);
        }
      }
    }

    return discounts;
  }

  public get inclusiveTaxesFromOrders(): Tax[] {
    const taxes: Tax[] = [];

    const orderMap = this.orderMap;

    const locationsIds = Object.keys(orderMap);
    for (const locationId of locationsIds) {
      if (orderMap[locationId].taxes) {
        for (const tax of orderMap[locationId].taxes) {
          if (tax.type === 'INCLUSIVE' && tax.applied_money.amount > 0) {
            taxes.push(tax);
          }
        }
      }
    }

    return taxes;
  }

  public get exclusiveTaxesFromOrders(): Tax[] {
    const taxes: Tax[] = [];

    const orderMap = this.orderMap;

    const locationsIds = Object.keys(orderMap);
    for (const locationId of locationsIds) {
      if (orderMap[locationId].taxes) {
        for (const tax of orderMap[locationId].taxes) {
          if (tax.type === 'ADDITIVE' && tax.applied_money.amount > 0) {
            taxes.push(tax);
          }
        }
      }
    }

    return taxes;
  }

  public get autoGratuityTaxes(): number {
    let autoGratuityTaxes = 0;

    const orderMap = this.orderMap;

    const locationsIds = Object.keys(orderMap);
    for (const locationId of locationsIds) {
      autoGratuityTaxes += orderMap[locationId].amount / 100 * this.autoGratuityPercent / 100;
    }

    return autoGratuityTaxes;
  }

  public get totalCartPrice(): number {
    const tips = this.paymentStore.createTabChecked ? 0 : !this.tab || this.tab?.is_closed ? this.tips : 0;
    return this.totalPrice + this.totalTaxesPrice + tips;
  }

  public get orderMap(): OrderMap {
    return this._orderMap.getValue();
  }

  public get countOrderMapRestaurants(): number {
    const locationIds = Object.keys(this.orderMap);
    return locationIds.length;
  }

  public get updateCartInProgress(): boolean {
    return this._updateCartInProgress.getValue();
  }

  public get tabClosedSuccessful(): boolean {
    return this._tabClosedSuccessful.getValue();
  }

  public get orderConfirmationText(): string {
    return this._orderConfirmationText;
  }

  public get discounts(): any[] {
    return this._discounts.value;
  }

  public getCoupons(): DiscountOrder[] {
    return this._coupons.value;
  }

  public get isLoyaltyProgramEnabled$(): Observable<boolean> {
    return this._loyaltyProgramEnabled.asObservable();
  }

  public set discounts(value: any[]) {
    this._discounts.next(value);
  }

  public set isFutureOrderError(isError: boolean) {
    this._isFutureOrderError.next(isError);
  }

  public setCoupons(coupon: DiscountOrder[]): void {
    this._coupons.next(coupon);
  }

  public get showTabModal(): boolean {
    return this._showTabModal.getValue();
  }

  public set tab(updatedTab: OrderTab) {
      this._tab.next(updatedTab);
  }

  public set loyalty(loyalty: Loyalty) {
    this._loyalty.next(loyalty);
  }

  public set groupTab(tab: GroupOrderTab) {
    this._groupTab.next(tab);
  }

  public set tabClosedSuccessful(value: boolean) {
    this._tabClosedSuccessful.next(value);
  }

  public set showTabModal(value: boolean) {
    this._showTabModal.next(value);
  }

  public set updateCartInProgress(value: boolean) {
    this._updateCartInProgress.next(value);
  }

  public set orderMap(value: OrderMap) {
    this._orderMap.next(value);
  }

  public setTip(tip: number, tipType: Tip = Tip.restaurant) {
    switch (tipType) {
      case Tip.restaurant: return this._tip.next(tip);
      case Tip.driver: return this._tipDriver.next(tip);
    }
  }

  public set pickupAt(pickupAt: string) {
    this._pickupAt.next(pickupAt);
  }

  public set pickupLocation(pickupLocation: string) {
    this._pickupLocation = pickupLocation;
  }

  public set deliveryAt(deliveryAt: string) {
    this._deliveryAt = deliveryAt;
  }

  public set isAsap(isAsap: boolean) {
    this._isAsap = isAsap;
  }

  public set printAheadDuration(printAheadDuration: string) {
    this._printAheadDuration = printAheadDuration;
  }

  public set pickupWindowDuration(pickupWindowDuration: string) {
    this._pickupWindowDuration = pickupWindowDuration;
  }

  public set autoGratuityPercent(autoGratuityPercent: number) {
    this._autoGratuityPercent = autoGratuityPercent;
  }

  public set driverGratuityPercent(driverGratuityPercent: number) {
    this._driverGratuityPercent = driverGratuityPercent;
  }

  public set enableRestaurantTips(enableTips: boolean) {
    this._enableRestaurantTip = enableTips;
  }

  public get enableRestaurantTips(): boolean {
    return this._enableRestaurantTip;
  }

  public set kyooFee(kyooFee: number) {
    this._kyooFee = kyooFee;
  }

  public set deliveryFee(kyooFee: number) {
    this._deliveryFee = kyooFee;
  }

  public set additionalKyooFee(additionalKyooFee: number) {
    this._additionalKyooFee = additionalKyooFee;
  }

  public set ordersInGroup(ordersInGroup: number) {
    this._ordersInGroup = ordersInGroup;
  }

  public set tippingEnabled(value: boolean) {
    this._tippingEnabled = value;
  }

  public set subscriptionsData(value: SubscriptionPhase[])  {
    this._subscriptionsData.next(value);
  }

  public set deliveryResponseData(value: DeliveryResponseData) {
    this._deliveryResponseData.next(value);
  }

  public set deliveryDurationData(value: DeliveryPreEstimateResponseData ) {
    this._deliveryDurationData.next(value);
  }

  public set isLoyaltyEnabled(value: boolean) {
    this._isLoyaltyEnabled.next(value);
  }

  public set orderConfirmationText(confirmationText: string) {
     this._orderConfirmationText = confirmationText;
  }

  public set isLoyaltyProgramEnabled(value: boolean) {
    this._loyaltyProgramEnabled.next(value);
  }

  public get textForCheckoutBtn(): string {
    if (this.updateCartInProgress) {
      return 'Cart updating...';
    }
      return `Checkout (${this.items.length} ${this.items.length > 1 ? 'items' : 'item'})`;
  }

  public addItemToCart(item: MenuItemForPayment): void {
    const savedItems = Object.assign([], this.items);
    savedItems.map(savedItem => {
      if (item && CartStore.compareToItem(savedItem, item)) {
        savedItem.item.quantity += item.item.quantity;
        savedItem.item.specialInstructions = item.item.specialInstructions;
        item = null;
      }
    });
    const updatedItems = item ? [...savedItems, item] : savedItems;
    this._items.next(updatedItems);
  }

  public taxesSum(taxes: Tax[]): number {
    let taxesAmountSum = 0;

    if (taxes) {
      for (const tax of taxes) {
        if (tax.type === 'ADDITIVE' && tax.applied_money.amount > 0) {
          taxesAmountSum += tax.applied_money.amount;
        }
      }
    }

    return taxesAmountSum;
  }

  public findCartItemIndex(item: MenuItemForPayment): number {
    return this.items.findIndex(savedItem => item && CartStore.compareToItem(savedItem, item));
  }

  public getVariationQuantityInCart(variationId: string): number {
    let quantity = 0;
    const itemsInCart = Object.assign([], this.items);
    itemsInCart.forEach(item => {
      item = item.item;
      if (item && item.variation && variationId && variationId.localeCompare(item.id) === 0) {
        quantity = item.quantity;
      }
    });
    return quantity;
  }

  public updateCartItemsListeners(): void {
    this._items.next(this.items);
  }

  updateLoyaltyPoints(): void {
    const locationsIds = Object.keys(this.orderMap);
    let pointsSum = 0;
    for (const locationId of locationsIds) {
      if (this.orderMap[locationId].loyaltyPoints) {
        pointsSum += this.orderMap[locationId].loyaltyPoints;
      }
    }
    this.loyalty = new Loyalty('loyalty point', pointsSum, pointsSum !== 0);
  }

  updateFee(orderMap, fee): void {
    orderMap.amount -= fee.total_money.amount;
    if (fee.total_discount_money.amount > 0) {
      orderMap.discounts[0].applied_money.amount -= fee.total_discount_money.amount;
    }
  }

  replaceCartItem(index: number, item: MenuItemForPayment): void {
    const savedItems = Object.assign([], this.items);
    savedItems[index] = item;
    this._items.next(savedItems);
  }

  public deleteItemFromCart(i: number): void {
    const curItems = this.items;
    const updatedItems = curItems.filter((item: MenuItemForPayment, j: number) => j !== i );
    this._items.next(updatedItems);
  }

  public deleteItemsFromCartByLocationId(locationId: string): void {
    const curItems = this.items;
    if (!locationId || !curItems) {
      this._items.next([]);
      return;
    }
    const updatedItems = curItems.filter(item => item.locationId !== locationId);
    this._items.next(updatedItems);
  }

  updateOrderMapData(locationId: string, order: Order, totalMoney: number, taxes: Tax[], discounts: Discount[],
                     loyaltyPoints: number, lineItems: any, estimated_duration: string = null): void {
    const updatedOrderMap = this.orderMap[locationId];
    updatedOrderMap.order = order;
    updatedOrderMap.amount = totalMoney;
    updatedOrderMap.discounts = discounts;
    updatedOrderMap.taxes = taxes;
    updatedOrderMap.loyaltyPoints = loyaltyPoints;
    updatedOrderMap.isUpdated = true;
    updatedOrderMap.estimatedDuration = parseDuration(estimated_duration);
    updatedOrderMap.estimatedDurationDefault = estimated_duration;
    const restaurantStore = this.injector.get(RestaurantStore);
    restaurantStore.getRestaurantById(locationId).pipe(take(1)).subscribe((restaurant) => {
      const serviceChargeName = restaurant.serviceChargeName;
      const kyooFee = lineItems?.find( (item) => item.name.includes(serviceChargeName ? serviceChargeName : `Kyoo Service Charge`));
      const deliveryFee = lineItems?.find( (item) => item.name.includes('Delivery fee'));
      if (kyooFee) {
        this.kyooFee = kyooFee.total_money.amount;
        this.updateFee(updatedOrderMap, kyooFee);
      } else if (deliveryFee) {
        this.deliveryFee = deliveryFee.total_money.amount;
        this.updateFee(updatedOrderMap, deliveryFee);
      }
      this.updateThrottling(lineItems);
      this.updateLoyaltyPoints();
      this.updateCartItemsListeners();
    });
  }

  private updateThrottling(lineItems): void {
    this.items.forEach(item => {
      const findItem = lineItems.find(lineItem => lineItem.catalog_object_id === item.item.id);
      item.throttlingEstimation = parseDuration(findItem?.estimated_duration);
    });
  }

  public clearCartAndOrderMap(): void {
     this._items.next([]);
     this.orderMap = {};
     this.updateCartInProgress = false;
  }
}

